import type { TFunction } from 'i18next';

import type { AdTagCreative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import {
  DEFAULT_AD_TAG_HTML,
  getCreativeThumbnailUrl,
} from '@feathr/extender/components/creatives/display.utils';

/**
 * Checks if a creative is an Ad Tag creative.
 *
 * @param creative - The creative to check
 * @returns True if the creative is an Ad Tag type
 */
export function isAdTagCreative(creative?: DisplayCreative): boolean {
  return creative?.get('_cls') === CreativeClass.DisplayAdTag;
}

/**
 * Checks if a creative is a video creative based on its class.
 *
 * @param creative - The creative to check
 * @returns True if the creative is a Video type
 */
export function isVideoCreative(creative?: DisplayCreative): boolean {
  const cls = creative?.get('_cls');
  return cls === CreativeClass.DisplayVideo || cls === CreativeClass.FacebookVideo;
}

/**
 * Gets the Ad Tag HTML content from an Ad Tag creative.
 *
 * @param creative - The creative to get HTML from
 * @returns The ad tag HTML, or undefined if not an Ad Tag creative
 */
export function getAdTagHtml(creative?: DisplayCreative): string | undefined {
  if (!isAdTagCreative(creative) || !creative) {
    return undefined;
  }
  return (creative as AdTagCreative).get('adtag');
}

/**
 * Validates an Ad Tag creative's HTML content.
 *
 * @param creative - The creative to validate
 * @param t - Translation function
 * @returns An array of validation error messages
 */
export function validateAdTagCreative(creative: DisplayCreative, t: TFunction): string[] {
  if (!isAdTagCreative(creative)) {
    return [];
  }

  const errors: string[] = [];
  const adtagHTML = (creative as AdTagCreative).get('adtag');

  if (!adtagHTML) {
    errors.push(t('Ad tag HTML cannot be empty'));
    return errors;
  }

  if (adtagHTML === DEFAULT_AD_TAG_HTML) {
    errors.push(t('Ad tag HTML must be customized'));
    return errors;
  }

  if (!adtagHTML.includes('@FEATHR_TRACK@')) {
    errors.push(t('Ad tag HTML must include the @FEATHR_TRACK@ impression tracking macro'));
  }

  if (!adtagHTML.includes('@FEATHR_CLICK@') && !adtagHTML.includes('@FEATHR_CLICK_ESC@')) {
    errors.push(
      t(
        'Ad tag HTML must include a click-through tracking macro: @FEATHR_CLICK@ or @FEATHR_CLICK_ESC@',
      ),
    );
  }

  return errors;
}

/**
 * Gets the URL for a creative's media content.
 *
 * @param creative - The creative to get the URL for
 * @returns The URL, or undefined if the creative is an Ad Tag or doesn't exist
 */
export function getCreativeMediaUrl(creative?: DisplayCreative): string | undefined {
  if (isAdTagCreative(creative) || !creative) {
    return undefined;
  }
  return getCreativeThumbnailUrl(creative);
}

/**
 * Gets the preview image URL based on creative type.
 * For videos, uses the video frame thumbnail URL if available.
 *
 * @param creative - The creative to get the preview for
 * @param videoFrameUrl - The extracted video frame URL (for video creatives)
 * @returns The preview image URL, or undefined
 */
export function getPreviewImageUrl(
  creative?: DisplayCreative,
  videoFrameUrl?: string,
): string | undefined {
  if (isVideoCreative(creative)) {
    return videoFrameUrl || undefined;
  }
  return getCreativeMediaUrl(creative);
}

/**
 * Gets the creative type label for display.
 *
 * @param creative - The creative to get the label for
 * @param t - Translation function
 * @returns The creative type label
 */
export function getCreativeTypeLabel(creative?: DisplayCreative, t?: TFunction): string {
  return creative?.creativeType ?? (t ? t('Image') : 'Image');
}
